import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  X as XIcon, Loader2, Save, School, Bus, Accessibility, Warehouse,
  Wrench, Building2, MapPin, Power, PowerOff, Database,
} from "lucide-react";

/**
 * V1 POI editor — simple popup to fix classification mistakes before the
 * Phase B "Arrêts" page redesign.
 *
 * Props:
 *   stop: full transit_stops document (must contain stop_id)
 *   onClose():       close the modal
 *   onSaved(fresh):  called after a successful save, with the new doc
 */
const TYPE_OPTIONS = [
  { value: "school",   label: "École",    Icon: School,        color: "#F59E0B" },
  { value: "stop",     label: "Arrêt",    Icon: Bus,           color: "#3B82F6" },
  { value: "pmr",      label: "PMR",      Icon: Accessibility, color: "#0EA5E9" },
  { value: "depot",    label: "Dépôt",    Icon: Warehouse,     color: "#71717A" },
  { value: "workshop", label: "Atelier",  Icon: Wrench,        color: "#71717A" },
  { value: "client",   label: "Client",   Icon: Building2,     color: "#10B981" },
  { value: "other",    label: "Autre",    Icon: MapPin,        color: "#A1A1AA" },
];

const SOURCE_LABEL = {
  navixy: "Logitrak (import API)",
  gtfs:   "GTFS (import standard)",
  logibus: "Logibus (manuel)",
  hrdf:   "HRDF (import Suisse)",
};

export default function POIEditorModal({ stop, onClose, onSaved }) {
  const [form, setForm] = useState({
    stop_name: stop.stop_name || "",
    address:   stop.address || "",
    city:      stop.city || "",
    postal_code: stop.postal_code || "",
    type:      stop.type || "stop",
    active:    stop.active !== false,
    notes:     stop.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      // Diff against stop so we only send what changed
      Object.entries(form).forEach(([k, v]) => {
        const original = stop[k] ?? (k === "active" ? true : "");
        if (v !== original) payload[k] = v;
      });
      if (!Object.keys(payload).length) {
        toast.info("Aucun changement à enregistrer");
        onClose();
        return;
      }
      const { data } = await api.patch(`/logibus/transit/stops/${stop.stop_id}`, payload);
      toast.success("POI mis à jour");
      onSaved?.(data.stop || data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  // Quick "suggest type" — runs the classifier on the current name/address
  // without persisting. The admin still has to click Save to apply.
  const suggestType = async () => {
    setSuggesting(true);
    try {
      // We piggy-back on the search endpoint: send the new name, see what
      // type the backend would assign. Cheap & avoids a dedicated endpoint.
      const { data } = await api.get(`/logibus/poi/search`, {
        params: { q: form.stop_name, limit: 1 },
      });
      const same = (data.items || []).find((i) => i.stop_id === stop.stop_id);
      if (same?.type) {
        setField("type", same.type);
        toast.success(`Suggestion : ${TYPE_OPTIONS.find((o) => o.value === same.type)?.label}`);
      } else {
        toast.info("Le classifier n'a pas changé d'avis sur ce POI.");
      }
    } catch { /* silent */ }
    finally { setSuggesting(false); }
  };

  const sourceLabel = SOURCE_LABEL[(stop.source || "").toLowerCase()] || (stop.source || "Inconnu");

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4"
      data-testid="poi-editor-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white border border-zinc-900 w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-[6px_6px_0_0_#0A0A0B]">
        <header className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between bg-[#FFC60A] sticky top-0 z-10">
          <div className="min-w-0">
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase">Modifier · POI</div>
            <h2 className="font-display font-black text-xl tracking-tighter mt-0.5 truncate" title={stop.stop_name}>
              {stop.stop_name}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="poi-editor-close"
            className="w-9 h-9 border border-zinc-900 bg-white hover:bg-zinc-100 flex items-center justify-center flex-shrink-0"
            aria-label="Fermer"
          >
            <XIcon size={14} />
          </button>
        </header>

        <div className="p-5 space-y-5">
          {/* Source — read-only */}
          <div className="border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs flex items-center gap-2" data-testid="poi-source">
            <Database size={12} className="text-zinc-500" />
            <span className="font-mono uppercase tracking-widest text-[10px] text-zinc-500">Source :</span>
            <strong>{sourceLabel}</strong>
            {stop.external_id && (
              <span className="font-mono text-[10px] text-zinc-500">· {stop.external_id}</span>
            )}
          </div>

          {/* Type — visual radio grid */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Type opérationnel</span>
              <button
                type="button"
                onClick={suggestType}
                disabled={suggesting}
                data-testid="poi-suggest-type"
                className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 underline disabled:opacity-50"
              >
                {suggesting ? "…" : "Suggérer auto"}
              </button>
            </div>
            <div className="grid grid-cols-4 gap-1.5" data-testid="poi-type-grid">
              {TYPE_OPTIONS.map((opt) => {
                const Icon = opt.Icon;
                const active = form.type === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setField("type", opt.value)}
                    data-testid={`poi-type-${opt.value}`}
                    className={`border p-2 flex flex-col items-center gap-1 text-[10px] font-mono uppercase tracking-widest transition-all ${
                      active
                        ? "border-zinc-900 bg-zinc-900 text-white shadow-[2px_2px_0_0_#FFC60A]"
                        : "border-zinc-300 bg-white hover:border-zinc-900"
                    }`}
                    style={active ? { boxShadow: `2px 2px 0 0 ${opt.color}` } : {}}
                  >
                    <Icon size={16} style={!active ? { color: opt.color } : undefined} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Name */}
          <Field
            label="Nom"
            value={form.stop_name}
            onChange={(v) => setField("stop_name", v)}
            testId="poi-name"
            placeholder="Ex : Collège de Beausobre"
          />

          {/* Address grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-3">
              <Field
                label="Adresse"
                value={form.address}
                onChange={(v) => setField("address", v)}
                testId="poi-address"
                placeholder="Rue, n°…"
              />
            </div>
            <Field
              label="Code postal"
              value={form.postal_code}
              onChange={(v) => setField("postal_code", v)}
              testId="poi-postal"
            />
            <div className="sm:col-span-2">
              <Field
                label="Ville"
                value={form.city}
                onChange={(v) => setField("city", v)}
                testId="poi-city"
              />
            </div>
          </div>

          {/* Notes */}
          <label className="block">
            <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Notes internes</span>
            <textarea
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              rows={2}
              data-testid="poi-notes"
              placeholder="Remarques d'exploitation, restrictions…"
              className="mt-1 w-full border border-zinc-900 px-3 py-2 font-mono text-sm bg-white focus:outline-none focus:bg-[#FFFBE6]"
            />
          </label>

          {/* Active toggle */}
          <button
            type="button"
            onClick={() => setField("active", !form.active)}
            data-testid="poi-active-toggle"
            className={`w-full border border-zinc-900 px-3 py-2 inline-flex items-center justify-between text-xs font-bold uppercase tracking-widest ${
              form.active ? "bg-emerald-50 hover:bg-emerald-100" : "bg-red-50 hover:bg-red-100"
            }`}
          >
            <span className="inline-flex items-center gap-2">
              {form.active ? <Power size={14} className="text-emerald-700" /> : <PowerOff size={14} className="text-red-700" />}
              POI {form.active ? "actif" : "inactif"}
            </span>
            <span className="text-[10px] text-zinc-600">Cliquer pour {form.active ? "désactiver" : "activer"}</span>
          </button>
        </div>

        <footer className="px-5 py-3 border-t border-zinc-900 flex justify-end gap-2 bg-zinc-50 sticky bottom-0">
          <button
            onClick={onClose}
            data-testid="poi-editor-cancel"
            className="border border-zinc-900 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-100"
          >
            Annuler
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="poi-editor-save"
            className="border border-zinc-900 bg-zinc-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Enregistrer
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, placeholder = "", type = "text" }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">{label}</span>
      <input
        type={type}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1 w-full border border-zinc-900 px-3 py-2 font-mono text-sm bg-white focus:outline-none focus:bg-[#FFFBE6]"
      />
    </label>
  );
}
